/**
 * PARTNER-VOICE FACTS (narration slice 1b; senior-partner review 2026-09-27 09:34, lead rulings
 * 09:40). A measure's voice file turns "{measure} for {scope} in {period}: {value}" into
 * "Information technology cost $126.5M in the general ledger in 2024." Every slot but the figure
 * is resolved here from typed parts (display-named scope, typed period, direction from the SAME
 * binding's sign × polarity); the `figure` grammar places the one bound figure. A family without
 * a voice keeps today's form (the caller falls back).
 */
import type { AskHelixNumericBindingDto } from '../../ask-helix';
import { directionPhrase, voiceSentence, type AskMeasureVoice } from './measure-voice';
import { coordinateAttribute, type AskPlanFact } from './narrative-plan';
import { periodLabel, snapshotDateLabel } from './period-label';

export type AskResolvedVoice = Readonly<{ voice: AskMeasureVoice; datasetRef: string | null }>;

type Timeframe = AskHelixNumericBindingDto['timeframe'];

/** "in 2024", "from 31 Dec 2025 to 5 Jul 2026", or "as at 14 Sep 2026" (typed dates only). */
export function periodPhrase(timeframe: Timeframe, style: AskMeasureVoice['periodStyle']): string {
  const oneDay = timeframe.start && timeframe.start === timeframe.end;
  if (style === 'stock' || timeframe.kind !== 'window' || oneDay) {
    const day = snapshotDateLabel({
      start: timeframe.asOf ?? timeframe.end,
      end: timeframe.asOf ?? timeframe.end,
    });
    if (day) return `as at ${day}`;
  }
  if (!timeframe.label) return '';
  const label = periodLabel(timeframe);
  return label.includes(' to ') ? `from ${label}` : `in ${label}`;
}

/** Fill every word slot; {v} stays for the grammar to place the bound figure. */
function fill(template: string, slots: Readonly<Record<string, string>>): string {
  return template
    .replace(/\{(scope|period|direction)\}/g, (_, slot: string) => slots[slot] ?? '')
    .replace(/\s+([.,])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The status phrase for a grouped row's typed coordinate, e.g. "needs a plan amendment". */
function statusOf(voice: AskMeasureVoice, binding: AskHelixNumericBindingDto): string | null {
  const coordinates =
    (binding as Readonly<{ coordinates?: Record<string, string> }>).coordinates ?? {};
  for (const [dimension, memberRef] of Object.entries(coordinates)) {
    const phrase = voice.statusPhrases?.[dimension]?.[memberRef];
    if (phrase) return phrase;
  }
  return null;
}

/**
 * A single figure in the family's voice. A grouped status row reads its status first and the
 * figure second: "{scope} needs a plan amendment. It has drifted $13.7M below … as at …".
 */
export function voiceFigureFact(
  binding: AskHelixNumericBindingDto,
  resolved: AskResolvedVoice,
  displayLabel: (raw: string) => string
): AskPlanFact | null {
  const { voice, datasetRef } = resolved;
  const template = voiceSentence(voice, datasetRef);
  const scope = displayLabel(binding.scope.label);
  const directed = template.includes('{direction}');
  if (directed && typeof binding.value !== 'number') return null;
  const status = statusOf(voice, binding);
  const attribute = status ? null : coordinateAttribute(binding);
  const lead = status ? `${scope} ${status}.` : attribute ? `${attribute}.` : null;
  const slots = {
    scope: status ? 'It' : scope,
    period: periodPhrase(binding.timeframe, voice.periodStyle),
    direction: directed ? directionPhrase(voice, binding.value as number) : '',
  };
  return {
    relation: 'figure',
    subject: scope,
    figures: { value: binding.placeholder },
    labels: {
      template: capitalized(fill(template, slots)),
      ...(lead ? { lead } : {}),
      ...(directed ? { magnitude: 'true' } : {}),
    },
    states: [],
    findingId: null,
  };
}

/**
 * A same-period gap in the actual side's voice: "{scope} is $1M behind plan in Aug 2026." The
 * sign is the actual side minus its reference, read from the engine's bound difference
 * (to − from), so the figure shows unsigned and the words carry the direction.
 */
export function voiceGapFact(
  change: AskHelixNumericBindingDto,
  sides: Readonly<{ from: AskResolvedVoice | null; to: AskResolvedVoice | null }>,
  displayLabel: (raw: string) => string
): AskPlanFact | null {
  const actualIsTo = sides.to?.voice.role === 'actual' && sides.from?.voice.role === 'reference';
  const actualIsFrom = sides.from?.voice.role === 'actual' && sides.to?.voice.role === 'reference';
  const actual = actualIsTo ? sides.to : actualIsFrom ? sides.from : null;
  if (!actual?.voice.gapSentence || typeof change.value !== 'number') return null;
  const signed = actualIsTo ? change.value : -change.value;
  const slots = {
    scope: displayLabel(change.scope.label),
    period: periodPhrase(change.timeframe, actual.voice.periodStyle),
    direction: directionPhrase(actual.voice, signed),
  };
  return {
    relation: 'figure',
    subject: slots.scope,
    figures: { value: change.placeholder },
    labels: { template: capitalized(fill(actual.voice.gapSentence, slots)), magnitude: 'true' },
    states: [],
    findingId: null,
  };
}

/** "the largest SG&A pool" for the ranking's grouping dimension, or null. */
export function voiceRankNoun(
  resolved: AskResolvedVoice | null,
  dimension: string | undefined,
  end: 'top' | 'bottom'
): string | null {
  if (!dimension || !resolved) return null;
  const { voice, datasetRef } = resolved;
  const datasetNoun = datasetRef
    ? voice.datasetRankNouns?.[datasetRef]?.[dimension]?.[end]
    : undefined;
  return datasetNoun ?? voice.rankNouns?.[dimension]?.[end] ?? null;
}
